import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FileText, Plus, Search, Tag, Loader2, AlertTriangle, Image as ImageIcon,
  Edit3, Trash2, Send, Archive, Eye, Calendar, Folder,
} from 'lucide-react';
import { blogAPI } from '../../services/api';
import BlogCategories from './BlogCategories';

/**
 * BlogManagement — Phase 6
 *
 * List view for blog posts with:
 *   - debounced search, status/category/tag filters
 *   - card-grid listing with per-card edit/publish-archive/delete actions
 *   - pagination, empty/loading/error states
 *   - categories management modal (BlogCategories)
 *
 * Create/edit navigation:
 *   /settings/website/blog/new — create
 *   /settings/website/blog/:id — edit
 */

const PAGE_SIZE = 12;

const STATUS_OPTIONS = [
  { value: '', label: 'كل الحالات' },
  { value: 'draft', label: 'مسودة' },
  { value: 'published', label: 'منشور' },
  { value: 'archived', label: 'مؤرشف' },
];

function statusBadgeClasses(status) {
  switch (status) {
    case 'published':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'draft':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'archived':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-500 border-gray-200';
  }
}

function statusLabel(status) {
  if (status === 'published') return 'منشور';
  if (status === 'draft') return 'مسودة';
  if (status === 'archived') return 'مؤرشف';
  return status || '—';
}

function formatRelativeAr(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHr = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffSec) < 60) return 'قبل لحظات';
    if (Math.abs(diffMin) < 60) return `قبل ${diffMin} د`;
    if (Math.abs(diffHr) < 24) return `قبل ${diffHr} س`;
    if (Math.abs(diffDay) < 30) return `قبل ${diffDay} يوم`;
    return d.toLocaleDateString('ar-SA');
  } catch {
    return '';
  }
}

export default function BlogManagement() {
  const navigate = useNavigate();

  // Filters / inputs
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Action state
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchCategories = useCallback(async () => {
    try {
      const resp = await blogAPI.listCategories();
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      const list = Array.isArray(data?.items) ? data.items : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCategories(list);
    } catch {
      // non-critical
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await blogAPI.list({
        q: q || undefined,
        status: status || undefined,
        categoryId: categoryId || undefined,
        tag: tag || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setTotalPages(Math.max(1, Number(data?.totalPages) || 1));
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(err?.response?.data?.message || 'فشل تحميل المقالات');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [q, status, categoryId, tag, page]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const categoryLookup = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c._id, c));
    return map;
  }, [categories]);

  const handlePublishToggle = async (post) => {
    setPendingActionId(post._id);
    try {
      if (post.status === 'published') {
        await blogAPI.archive(post._id);
        toast.success('تم أرشفة المقال');
      } else {
        await blogAPI.publish(post._id);
        toast.success('تم نشر المقال');
      }
      await fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل تنفيذ العملية');
    } finally {
      if (aliveRef.current) setPendingActionId(null);
    }
  };

  const handleDelete = async (post) => {
    setPendingActionId(post._id);
    try {
      await blogAPI.remove(post._id);
      toast.success('تم حذف المقال');
      setConfirmDelete(null);
      await fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
    } finally {
      if (aliveRef.current) setPendingActionId(null);
    }
  };

  const hasActiveFilters = !!(q || status || categoryId || tag);
  const clearFilters = () => {
    setSearchInput('');
    setQ('');
    setStatus('');
    setCategoryId('');
    setTag('');
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">المدونة</h1>
            </div>
            <p className="text-gray-500 text-sm mr-1">
              انشر مقالات طبية موجّهة لعملائك وحسّن ظهور موقعك في محركات البحث
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCategoriesOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <Tag className="w-4 h-4" />
              التصنيفات
            </button>
            <button
              onClick={() => navigate('/settings/website/blog/new')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              مقال جديد
            </button>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="ابحث في العنوان والمحتوى..."
                className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={categoryId}
              onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">كل التصنيفات</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name?.ar || c.name?.en || c.slug}
                </option>
              ))}
            </select>
          </div>
          <div className="grid md:grid-cols-4 gap-3 mt-3">
            <input
              value={tag}
              onChange={(e) => { setTag(e.target.value); setPage(1); }}
              placeholder="وسم (tag) واحد للتصفية..."
              className="md:col-span-3 w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              مسح التصفية
            </button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
        ) : loadError ? (
          <div className="p-8 rounded-2xl border border-blue-100 bg-blue-50 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-blue-500" />
            <p className="text-sm text-blue-700">{loadError}</p>
            <button
              onClick={fetchItems}
              className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
            >
              أعد المحاولة
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
            <FileText className="w-12 h-12 mx-auto mb-3" />
            {hasActiveFilters ? (
              <>
                <p className="text-sm">لا توجد نتائج مطابقة</p>
                <button
                  onClick={clearFilters}
                  className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
                >
                  مسح التصفية
                </button>
              </>
            ) : (
              <>
                <p className="text-sm">لا توجد مقالات بعد</p>
                <button
                  onClick={() => navigate('/settings/website/blog/new')}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
                >
                  <Plus className="w-4 h-4" />
                  إنشاء أول مقال
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((post) => {
                const cat = post.categoryId && typeof post.categoryId === 'object'
                  ? post.categoryId
                  : categoryLookup.get(post.categoryId) || null;
                const title = post.title?.ar || post.title?.en || '(بدون عنوان)';
                const titleEn = post.title?.en && post.title?.ar ? post.title.en : '';
                const tags = Array.isArray(post.tags) ? post.tags : [];
                const busy = pendingActionId === post._id;
                return (
                  <motion.div
                    key={post._id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col"
                  >
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      {post.featuredImageUrl ? (
                        <img
                          src={post.featuredImageUrl}
                          alt={post.featuredImageAlt || title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-10 h-10" />
                        </div>
                      )}
                      <span
                        className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadgeClasses(post.status)}`}
                      >
                        {statusLabel(post.status)}
                      </span>
                    </div>

                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-base font-bold text-gray-800 line-clamp-2">
                        {title}
                      </h3>
                      {titleEn && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-1" dir="ltr">{titleEn}</p>
                      )}

                      <div className="flex items-center gap-2 flex-wrap mt-3 text-xs">
                        {cat && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                            <Folder className="w-3 h-3" />
                            {cat.name?.ar || cat.name?.en || cat.slug}
                          </span>
                        )}
                        {tags.slice(0, 3).map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-100">
                            #{t}
                          </span>
                        ))}
                        {tags.length > 3 && (
                          <span className="text-gray-400">...</span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-3">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatRelativeAr(post.publishedAt || post.updatedAt || post.createdAt) || '—'}
                        </span>
                        {typeof post.viewCount === 'number' && (
                          <span className="inline-flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.viewCount}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 mt-4 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => navigate(`/settings/website/blog/${post._id}`)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          تعديل
                        </button>
                        <button
                          onClick={() => handlePublishToggle(post)}
                          disabled={busy}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
                            post.status === 'published'
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }`}
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : post.status === 'published' ? (
                            <><Archive className="w-3.5 h-3.5" /> أرشفة</>
                          ) : (
                            <><Send className="w-3.5 h-3.5" /> نشر</>
                          )}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(post)}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >السابق</button>
                <span className="text-sm text-gray-500 mx-2">
                  صفحة {page} من {totalPages} — {total} مقال
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >التالي</button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">تأكيد حذف المقال</h3>
                <p className="text-sm text-gray-600 mt-1">
                  سيتم حذف &quot;{confirmDelete.title?.ar || confirmDelete.title?.en || confirmDelete.slug}&quot; نهائياً. لا يمكن التراجع.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={pendingActionId === confirmDelete._id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {pendingActionId === confirmDelete._id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories modal */}
      <BlogCategories
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        onChanged={fetchCategories}
      />
    </div>
  );
}
